import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"
import { cn } from "@/lib/utils"

const typographyVariants = cva("text-foreground", {
  variants: {
    size: {
      "4xl": "text-4xl font-bold tracking-tight",
      "3xl": "text-3xl font-bold leading-9",
      "2xl": "text-2xl font-bold leading-8",
      xl: "text-xl font-bold leading-7",
      lg: "text-lg tracking-tight",
      md: "text-md leading-6",
      sm: "text-sm leading-5",
    },
  },
  defaultVariants: {
    size: "md",
  },
})

type Element = keyof React.JSX.IntrinsicElements

type TypographyProps<T extends Element> = {
  as: T
} & VariantProps<typeof typographyVariants> &
  React.HTMLAttributes<HTMLElement>

const Typography = React.forwardRef(
  <T extends Element>(
    { className, as, size, ...props }: TypographyProps<T>,
    ref: React.ForwardedRef<HTMLElement>,
  ) => {
    const Component = as || "p"
    const componentProps = {
      className: cn(typographyVariants({ size, className })),
      ref,
      ...props,
    }
    return React.createElement(Component, componentProps)
  },
)

Typography.displayName = "Typography"

export { Typography, typographyVariants }
